import React, { useState } from 'react'

import Calendar from '@/components/Calendar/Calendar'
import Modal from '@/components/Modal/Modal'
import Button, { ColorButton } from '@/components/Button/Button'
import FilterButton from '@/components/FilterButton/FilterButton'
import Typography from '@/components/Typography/Typography'

import * as styles from './CalendarModal.css'
import { DateRange } from 'react-day-picker'
import { format } from 'date-fns'
import { getDatePeriods } from '../Calendar/getDatePeriods'
import { Swiper, SwiperSlide } from 'swiper/react'

const formatDateRangeLabel = ({
  localDate,
}: {
  localDate: DateRange | undefined
}) => {
  if (localDate?.from && localDate?.to) {
    const from = format(localDate.from, 'dd/MM/yyyy')
    const to = format(localDate.to, 'dd/MM/yyyy')
    const fromKeyword = format(localDate.from, 'yyyy-MM-dd')
    const toKeyword = format(localDate.to, 'yyyy-MM-dd')
    return {
      label: `${from} à ${to}`,
      keyword: `${fromKeyword},${toKeyword}`,
    }
  } else if (localDate?.from) {
    const from = format(localDate.from, 'dd/MM/yyyy')
    const fromKeyword = format(localDate.from, 'yyyy-MM-dd')
    return {
      label: from,
      keyword: `${fromKeyword}`,
    }
  }
  return null
}

type CalendarModalProps = {
  open: boolean
  title: string | React.ReactNode
  accessibilityTitle: string
  hideModal(): void
  onSelect?(...args: unknown[]): void
  isMobile?: boolean
  container?: HTMLElement | null
  colorFilterButton?: ColorButton
  colorButton?: ColorButton
}

const CalendarModal: React.FC<CalendarModalProps> = ({
  open,
  hideModal,
  onSelect,
  title,
  accessibilityTitle,
  isMobile = false,
  container = null,
  colorFilterButton = 'brandPale',
  colorButton,
}) => {
  const [localDate, setLocalDate] = useState<DateRange | undefined>(undefined)
  const [selectedBtn, setSelectedBtn] = useState<number>(0)
  const removeFilter = () => {
    setLocalDate(undefined)
    setSelectedBtn(0)
  }

  const filterDate = () => {
    const formattedDateRange = formatDateRangeLabel({ localDate })
    onSelect?.(formattedDateRange)
    handleHideModal()
  }

  const handleHideModal = () => {
    removeFilter()
    hideModal()
  }

  const datePeriods = getDatePeriods()

  const buttonDate = datePeriods.map((item, index) => {
    const { label, period } = item
    return {
      label: label.charAt(0).toUpperCase() + label.slice(1),
      indexBtn: index + 1,
      handleClick: () => {
        setLocalDate(period)
        setSelectedBtn(index + 1)
      },
    }
  })

  return (
    <Modal
      accessibilityTitle={accessibilityTitle}
      width={680}
      open={open}
      onClose={handleHideModal}
      container={container}
      hasSizePadding={false}
    >
      <div className={styles.CalendarModalContainer}>
        <div className={styles.CalendarModalText}>
          <Typography component="h3" type="medium" variant="1" weight="bold">
            {title}
          </Typography>
          <p className={styles.CalendarModalDescription}>
            Selecione uma ou mais datas
          </p>
        </div>

        <div className={styles.CalendarModalContent}>
          <div className={styles.DatesContent}>
            <Swiper slidesPerView={'auto'} spaceBetween={8}>
              {buttonDate.map((itemDate) => {
                const { label, indexBtn, handleClick } = itemDate
                return (
                  <SwiperSlide key={label}>
                    <FilterButton
                      selectedItem={selectedBtn === indexBtn ? label : ''}
                      onClick={
                        selectedBtn === indexBtn ? removeFilter : handleClick
                      }
                      removeSelectedItem={removeFilter}
                      btnType="date"
                      upperCase={false}
                      color={colorFilterButton}
                    >
                      {label}
                    </FilterButton>
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </div>
          <div
            className={styles.CalendarContent}
            id={`modal-calendar-${colorFilterButton}`}
          >
            <Calendar
              numberOfMonths={isMobile ? 1 : 2}
              mode="range"
              selected={localDate}
              onSelect={setLocalDate}
            />
          </div>
        </div>
        <div className={styles.CalendarModalContentButton}>
          {localDate && (
            <div className={styles.ButtonCancel}>
              <Button
                onClick={removeFilter}
                variant="text"
                upperCase={false}
                color={colorButton || 'neutral'}
              >
                Limpar
              </Button>
            </div>
          )}
          <div className={styles.ButtonResult}>
            <Button
              onClick={filterDate}
              upperCase={false}
              disabled={!localDate}
              fullWidthOnMobile
              color={colorButton || 'brand'}
            >
              Mostrar resultados
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default CalendarModal
